import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { sharedApi, getAuthToken } from "../../../services/sharedApi";
import { getCurrentUser } from "../../../store/slices/authSlice";
import { toApiError } from "../../../utils/errorUtils";

import type {
  StudentActivity,
  StudentReport,
  StudentReportOptions,
  StudentsResponse,
  TeacherInvitationPreview,
  TeacherJoinLink,
} from "@lingoshade/domain";

interface StudentsState {
  teacher: StudentsResponse;
  student: StudentsResponse;
  link: TeacherJoinLink | null;
  preview: TeacherInvitationPreview | null;
  activity: StudentActivity | null;
  activityId: string | null;
  report: StudentReport | null;
  reportRequestId: string | null;
  reportBusy: boolean;
  reportError: string | null;
  previewValue: string | null;
  busy: boolean;
  error: string | null;
  incomingRequestId: string | null;
}
const empty: StudentsResponse = {
  relationships: [],
  acceptedCount: 0,
  limit: 30,
};
const initialState: StudentsState = {
  teacher: empty,
  student: empty,
  link: null,
  preview: null,
  activity: null,
  activityId: null,
  report: null,
  reportRequestId: null,
  reportBusy: false,
  reportError: null,
  previewValue: null,
  busy: false,
  error: null,
  incomingRequestId: null,
};
function code(error: unknown): string {
  return toApiError(error).code || "STUDENT_TECHNICAL_ERROR";
}

export const fetchTeacherInvitations = createAsyncThunk<
  StudentsResponse,
  void,
  { rejectValue: string }
>(
  "teacherInvitations/list",
  async (
    _,
    { rejectWithValue },
  ): Promise<StudentsResponse | ReturnType<typeof rejectWithValue>> => {
    try {
      return await sharedApi.students.list("student", getAuthToken());
    } catch (error: unknown) {
      return rejectWithValue(code(error));
    }
  },
);

export const fetchStudents = createAsyncThunk<
  { teacher: StudentsResponse; student: StudentsResponse },
  void,
  { rejectValue: string }
>(
  "students/list",
  async (
    _,
    { rejectWithValue },
  ): Promise<
    | { teacher: StudentsResponse; student: StudentsResponse }
    | ReturnType<typeof rejectWithValue>
  > => {
    try {
      const [teacher, student] = await Promise.all([
        sharedApi.students.list(
          "teacher",
          getAuthToken(),
          Intl.DateTimeFormat().resolvedOptions().timeZone,
        ),
        sharedApi.students.list("student", getAuthToken()),
      ]);
      return { teacher, student };
    } catch (error: unknown) {
      return rejectWithValue(code(error));
    }
  },
);
type StudentOperation =
  | { type: "invite"; email: string }
  | { type: "join"; value: string; fullActivityConsent?: boolean }
  | { type: "accept"; id: string; role: "teacher" | "student" }
  | {
      type: "update";
      id: string;
      action: "remove" | "reject" | "request-full" | "allow-full" | "deny-full";
      invitationToken?: string;
    };
export const changeStudent = createAsyncThunk<
  void,
  StudentOperation,
  { rejectValue: string }
>(
  "students/change",
  async (
    operation,
    { dispatch, rejectWithValue },
  ): Promise<void | ReturnType<typeof rejectWithValue>> => {
    try {
      const token = getAuthToken();
      switch (operation.type) {
        case "invite":
          await sharedApi.students.invite(operation.email, token);
          break;
        case "join":
          await sharedApi.students.join(operation.value, token, {
            fullActivityConsent: operation.fullActivityConsent,
          });
          await dispatch(getCurrentUser());
          break;
        case "accept":
          await sharedApi.students.accept(operation.id, operation.role, token);
          break;
        case "update":
          await sharedApi.students.update(
            operation.id,
            operation.action,
            token,
            operation.invitationToken,
          );
          break;
      }
      await dispatch(fetchStudents()).unwrap();
    } catch (error: unknown) {
      return rejectWithValue(code(error));
    }
  },
);
export const generateJoinLink = createAsyncThunk<
  TeacherJoinLink,
  void,
  { rejectValue: string }
>(
  "students/link",
  async (
    _,
    { rejectWithValue },
  ): Promise<TeacherJoinLink | ReturnType<typeof rejectWithValue>> => {
    try {
      return await sharedApi.students.joinLink(getAuthToken());
    } catch (error: unknown) {
      return rejectWithValue(code(error));
    }
  },
);
export const previewTeacher = createAsyncThunk<
  TeacherInvitationPreview,
  string,
  { rejectValue: string }
>(
  "students/preview",
  async (
    value,
    { rejectWithValue },
  ): Promise<TeacherInvitationPreview | ReturnType<typeof rejectWithValue>> => {
    try {
      return await sharedApi.students.preview(value, getAuthToken());
    } catch (error: unknown) {
      return rejectWithValue(code(error));
    }
  },
);
export const fetchStudentActivity = createAsyncThunk<
  StudentActivity,
  { id: string; offset?: number },
  { rejectValue: string; state: { students: StudentsState } }
>(
  "students/activity",
  async (
    { id, offset },
    { rejectWithValue, getState },
  ): Promise<StudentActivity | ReturnType<typeof rejectWithValue>> => {
    try {
      const result = await sharedApi.students.activity(
        id,
        getAuthToken(),
        offset,
      );
      const previous = getState().students.activity;
      if (offset && previous && previous.scopeKey !== result.scopeKey)
        return await sharedApi.students.activity(id, getAuthToken(), 0);
      return result;
    } catch (error: unknown) {
      return rejectWithValue(code(error));
    }
  },
);
export const fetchStudentReport = createAsyncThunk<
  StudentReport,
  { id: string; options?: StudentReportOptions },
  { rejectValue: string }
>(
  "students/report",
  async (
    { id, options },
    { rejectWithValue },
  ): Promise<StudentReport | ReturnType<typeof rejectWithValue>> => {
    try {
      return await sharedApi.students.report(id, options, getAuthToken());
    } catch (error: unknown) {
      return rejectWithValue(code(error));
    }
  },
);
const slice = createSlice({
  name: "students",
  initialState,
  reducers: {
    resetStudents: (): StudentsState => initialState,
    closeStudentActivity: (state): void => {
      state.activity = null;
      state.activityId = null;
      state.report = null;
      state.reportRequestId = null;
      state.reportBusy = false;
      state.reportError = null;
    },
  },
  extraReducers: (builder): void => {
    builder.addCase(fetchStudentReport.pending, (state, action): void => {
      state.activityId = action.meta.arg.id;
      state.reportRequestId = action.meta.requestId;
      state.reportBusy = true;
      state.reportError = null;
      state.report = null;
    });
    builder.addCase(fetchStudentReport.fulfilled, (state, action): void => {
      if (state.reportRequestId === action.meta.requestId) {
        state.report = action.payload;
        state.reportBusy = false;
      }
    });
    builder.addCase(fetchStudentReport.rejected, (state, action): void => {
      if (state.reportRequestId === action.meta.requestId) {
        state.report = null;
        state.reportBusy = false;
        state.reportError = action.payload || "STUDENT_TECHNICAL_ERROR";
      }
    });
    builder.addCase(fetchTeacherInvitations.pending, (state, action): void => {
      state.incomingRequestId = action.meta.requestId;
    });
    builder.addCase(
      fetchTeacherInvitations.fulfilled,
      (state, action): void => {
        if (state.incomingRequestId === action.meta.requestId)
          state.student = action.payload;
      },
    );
    builder.addCase(fetchStudents.fulfilled, (state, action): void => {
      state.incomingRequestId = null;
      state.teacher = action.payload.teacher;
      state.student = action.payload.student;
    });
    builder.addCase(generateJoinLink.fulfilled, (state, action): void => {
      state.link = action.payload;
    });
    builder.addCase(previewTeacher.pending, (state, action): void => {
      state.preview = null;
      state.previewValue = action.meta.arg;
    });
    builder.addCase(previewTeacher.fulfilled, (state, action): void => {
      if (state.previewValue === action.meta.arg)
        state.preview = action.payload;
    });
    builder.addCase(fetchStudentActivity.pending, (state, action): void => {
      if (!action.meta.arg.offset) state.activity = null;
      state.activityId = action.meta.arg.id;
    });
    builder.addCase(fetchStudentActivity.fulfilled, (state, action): void => {
      if (state.activityId !== action.meta.arg.id) return;
      if (
        action.meta.arg.offset &&
        state.activity &&
        state.activity.scopeKey === action.payload.scopeKey
      ) {
        state.activity.practice.push(...action.payload.practice);
        state.activity.dictations.push(...action.payload.dictations);
        state.activity.words.push(...action.payload.words);
        state.activity.reviews.push(...action.payload.reviews);
        state.activity.hasMore = action.payload.hasMore;
        state.activity.nextOffset = action.payload.nextOffset;
      } else state.activity = action.payload;
    });
    builder.addCase(fetchStudentActivity.rejected, (state, action): void => {
      if (
        state.activityId === action.meta.arg.id &&
        ["INVITATION_UNAVAILABLE", "PRO_REQUIRED"].includes(
          action.payload || "",
        )
      )
        state.activity = null;
    });
    builder.addMatcher(
      (action): boolean =>
        action.type.startsWith("students/") &&
        !action.type.startsWith("students/report/") &&
        action.type.endsWith("/pending"),
      (state): void => {
        state.busy = true;
        state.error = null;
      },
    );
    builder.addMatcher(
      (action): boolean =>
        action.type.startsWith("students/") &&
        !action.type.startsWith("students/report/") &&
        action.type.endsWith("/fulfilled"),
      (state): void => {
        state.busy = false;
      },
    );
    builder.addMatcher(
      (action): action is { type: string; payload?: string } =>
        action.type.startsWith("students/") &&
        !action.type.startsWith("students/report/") &&
        action.type.endsWith("/rejected"),
      (state, action): void => {
        state.busy = false;
        state.error = action.payload || "STUDENT_TECHNICAL_ERROR";
      },
    );
  },
});
export const { resetStudents, closeStudentActivity } = slice.actions;
export default slice.reducer;
